import { useState } from 'react';
import './ExpertCard.scss';
import { FiEdit } from "react-icons/fi";
import { FaUserCircle } from "react-icons/fa";
import CommentPopup, { CommentTrigger, MOCK_COMMENTS } from '../CommentPopup/CommentPopup';

const STATUS_LABEL = {
  LIVE: 'Live',
  YET_TO_LIVE: 'Yet to Live',
  UPDATED: 'Updated',
  CLOSED: 'Closed',
  EXPIRED: 'Expired',
};

const STATUS_CLASS = {
  LIVE: 'status-active',
  YET_TO_LIVE: 'status-yet',
  UPDATED: 'status-updated',
  CLOSED: 'status-closed',
  EXPIRED: 'status-closed',
};

export default function ExpertCard({ data, onEdit, onExit }) {
  const [showMobileDetails, setShowMobileDetails] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState(MOCK_COMMENTS);

  const statusLabel = STATUS_LABEL[data.status] || (data.status ? data.status.replace(/_/g, ' ') : 'Unknown');
  const statusClass = STATUS_CLASS[data.status] || 'status-active';

  const min = data.risk;
  const max = data.resistanceHigh;
  const cmpPercent = Math.min(100, Math.max(0, ((data.cmp - min) / (max - min)) * 100));

  const entryMid = (Number(data.supportLow) + Number(data.supportHigh)) / 2;
  const calcLevelPct = (target) => {
    if (!entryMid || !target) return null;
    const pct = data.direction === 'SHORT'
      ? ((entryMid - Number(target)) / entryMid) * 100
      : ((Number(target) - entryMid) / entryMid) * 100;
    return pct.toFixed(2);
  };
  const level1Pct = calcLevelPct(data.resistanceLow);
  const level2Pct = calcLevelPct(data.resistanceHigh);

  const handleAddComment = (comment) => {
    setComments(prev => [comment, ...prev]);
  };

  return (
    <div className="expert-card">
      <div className="card-top">
        {/* LEFT */}
        <div className="section section-left">

          {/* WRAP FOR FLEX */}
          <div className="mobile-header">

            <div className="coin-wrap">
              <div className="coin-icon">{data.symbol}</div>
              <div>
                <div className="coin-title">
                  {data.coin} <span className="info-icon">ⓘ</span>
                </div>

                <div className="price-row">
                  ${(+data.price).toFixed(2)}
                  <span className="positive">
                    +{(+data.change).toFixed(2)} ({(+data.percent).toFixed(2)}%)
                  </span>
                </div>

                <div className="date">{data.date}</div>
              </div>
            </div>

            {/* MOBILE ACTIONS */}
            {onExit && (
              <div className="mobile-actions">
                <button className="exit-btn" onClick={onExit}>Exit</button>
              </div>
            )}

            {/* COMMENT TRIGGER — mobile only, beside script name */}
            <div className="comment-trigger-wrap mobile-only-trigger">
              <CommentTrigger count={comments.length} onClick={() => setShowComments(true)} />
            </div>

          </div>

          {/* WEB-ONLY ICON BUTTONS - HIDDEN
          <div className="left-icon-row web-only">
            <div className="icon-btn"><img src={FundamentalIcon} alt="Fundamental Analysis" /></div>
            <div className="icon-btn"><img src={LogicIcon} alt="Logic" /></div>
          </div>
          */}

          {/* BELOW COIN WRAP */}
          <div className="mobile-bottom">
            <div className="mobile-status">
              Status : <span className={statusClass}>{statusLabel}</span>
            </div>

           <div className="mobile-gap">
            <div className="gap-label">Gap to Level</div>
            <span className="gap-value">{data.gap}%</span>
           </div>
          </div>

        </div>

        <div className="divider" />

        {/* MIDDLE */}
        <div className="section section-middle">
          <div className="zones-row">
            <div className="zone-item">
              <div className="zone-title">Risk area <span className="info-icon">ⓘ</span></div>
              <div className="zone-value risk">${data.risk}</div>
            </div>

            <div className="zone-divider" />

            <div className="zone-item">
              <div className="zone-title">Support Zone <span className="info-icon">ⓘ</span></div>
              <div className="zone-value support">
                ${data.supportLow} | ${data.supportHigh}
              </div>
            </div>

            <div className="zone-divider" />

            <div className="zone-item">
              <div className="zone-title">Resistance Zone <span className="info-icon">ⓘ</span></div>
              <div className="zone-value resistance">
                ${data.resistanceLow} | ${data.resistanceHigh}
              </div>
            </div>
          </div>

          <div className="middle-bottom">
            <div className="gap-col">
              <div className="gap-col-label">Gap to Level</div>
              <span className="gap-col-value">{data.gap}%</span>
            </div>

            {/* HIDDEN: AI score
            <div className="ai-score-wrap web-only">
              <div className="ai-score-label">Check AI Score</div>
              <div className="ai-gauge">
                <div className="ai-gauge-arc" />
                <div className="ai-gauge-center" />
                <div className="ai-gauge-needle" />
              </div>
            </div>
            */}

            <button className={`buy-btn web-only${data.direction === 'SHORT' ? ' sell-btn' : ''}`}>
              {data.direction === 'SHORT' ? 'Sell / Short' : 'Buy / Long'}
            </button>
          </div>
        </div>

        <div className="divider" />

        {/* RIGHT */}
        <div className="section section-right">
          {onExit && (
            <div className="top-actions">
              <button className="exit-btn" onClick={onExit}>Exit</button>
            </div>
          )}

          <div className="cmp-bar">
            <div className="cmp-pointer" style={{ left: `${cmpPercent}%`, transform: `translateX(-${cmpPercent}%)` }}>
              <div className="cmp-label">CMP</div>
              <div className="cmp-label-price">${data.cmp}</div>
              <div className="cmp-arrow">▼</div>
            </div>
            <div className="cmp-track">
              <span className="pill risk">Risk area</span>
              <span className="pill support">Support Zone</span>
              <span className="pill resistance">Resistance Zone</span>
            </div>
          </div>

          {/* COMMENT TRIGGER — web only */}
          <div className="comment-trigger-wrap web-only">
            <CommentTrigger count={comments.length} onClick={() => setShowComments(true)} />
          </div>

          {/* HIDDEN: thumbs up/down
          <div className="thumbs-row">
            <span className="thumbs-down"><img src={DislikeIcon} alt="Dislike" /> {data.thumbsDown}</span>
            <span className="thumbs-up">{data.thumbsUp} <img src={LikeIcon} alt="Like" /></span>
          </div>
          */}
        </div>
      </div>

      {/* MOBILE VIEW DETAILS */}
      <div
        className="view-details mobile-only"
        onClick={() =>
          setShowMobileDetails(showMobileDetails === data.id ? null : data.id)
        }
      >
        <div className="view-left">
          {data.profilePic
            ? <img src={data.profilePic} alt="profile" className="profile-pic" />
            : <FaUserCircle className="profile-pic" />
          }
          <span className="mobile-date">{data.date}</span>
        </div>

        <div className="view-right">
          View Details
          <span className={`arrow ${showMobileDetails === data.id ? 'up' : ''}`}>
            ▾
          </span>
        </div>
      </div>

      {showMobileDetails === data.id && (
        <div className="mobile-details">
          <div className="zones-row">
            <div className="zone-item">
              <div className="zone-title">Risk area</div>
              <div className="zone-value risk">${data.risk}</div>
            </div>

            <div className="zone-item">
              <div className="zone-title">Support Zone</div>
              <div className="zone-value support">
                ${data.supportLow} | ${data.supportHigh}
              </div>
            </div>

            <div className="zone-item">
              <div className="zone-title">Resistance Zone</div>
              <div className="zone-value resistance">
                ${data.resistanceLow} | ${data.resistanceHigh}
              </div>
            </div>
          </div>

          <div className="cmp-mobile cmp-bar">
            <div className="cmp-pointer" style={{ left: `${cmpPercent}%`, transform: `translateX(-${cmpPercent}%)` }}>
              <div className="cmp-label">CMP</div>
              <div className="cmp-label-price">${data.cmp}</div>
              <div className="cmp-arrow">▼</div>
            </div>
            <div className="cmp-track">
              <span className="pill risk">Risk area</span>
              <span className="pill support">Support Zone</span>
              <span className="pill resistance">Resistance Zone</span>
            </div>
          </div>
        </div>
      )}

      {/* COMMENT POPUP */}
      <CommentPopup
        open={showComments}
        onClose={() => setShowComments(false)}
        comments={comments}
        onAddComment={handleAddComment}
      />

      {/* FOOTER */}
      <div className="card-footer single-line-footer">
        <div className="footer-left">
          <div className="footer-ticker">
            <span className="green-dot" />
            <span className="footer-text-item">
              Level 1 Reached at {level1Pct !== null ? `${level1Pct}%` : '—'}
            </span>

            <span className="green-dot small" />

            <span className="footer-text-item">
              Level 2 Reached at {level2Pct !== null ? `${level2Pct}%` : '—'}
            </span>
          </div>
        </div>

        <div className="footer-right">
          <span className="status">
            Status :<span className={statusClass}> {statusLabel}</span>
          </span>
          {data.leverage && (
            <span className="last-update">Leverage: {data.leverage}x</span>
          )}
          <span className="last-update">{data.date}</span>
          {data.expertName && (
            <span className="last-update" style={{ fontWeight: 600 }}>{data.expertName}</span>
          )}
          {data.profilePic
            ? <img src={data.profilePic} alt="profile" className="profile-pic" />
            : <FaUserCircle className="profile-pic" />
          }
        </div>
      </div>
    </div>
  );
}
